// src/services/PaymentSuccess.jsx
import React, { useState, useEffect } from "react";
import firebase from "firebase/app";
import "firebase/firestore";
import "firebase/auth";

const PaymentSuccess = () => {
    const [status, setStatus] = useState("loading");
    const [message, setMessage] = useState("");
    const [debugInfo, setDebugInfo] = useState("");

    useEffect(() => {
        processPayment();
    }, []);

    const processPayment = async () => {
        setDebugInfo('Démarrage du processus...');

        try {
            const token = getTokenFromURL();
            setDebugInfo('Token récupéré');

            if (!token) {
                throw new Error('Token manquant');
            }
            const storedData = localStorage.getItem('pendingSubscription');
            setDebugInfo('Données localStorage récupérées');

            if (!storedData) {
                throw new Error('Données de paiement manquantes');
            }

            const paymentData = JSON.parse(storedData);

            setDebugInfo('Vérification du paiement...');
            let paymentStatus = null;

            try {
                paymentStatus = await checkLigdicashPayment(token);

                if (paymentStatus && paymentStatus.status === 'completed') {
                    setDebugInfo('Paiement confirmé par LigdiCash');
                }
            } catch (apiError) {

            }
            setDebugInfo('Vérification de votre connexion...');
            const user = await waitForUser();

            //  Activer l'abonnement
            setDebugInfo('Activation de votre abonnement...');

            try {
                await activateMongoDBSubscription(user, paymentData.planId, paymentStatus);
                setDebugInfo('MongoDB activé');
            } catch (mongoError) {

            }

            //  Mettre à jour Firestore
            await updateFirestoreSubscription(user.uid, paymentData.planId);
            setDebugInfo('Firestore mis à jour');

            //SUCCÈS
            localStorage.removeItem('pendingSubscription');

            const planNames = {
                monthly: "Mensuel",
                quarterly: "Trimestriel",
                annual: "Annuel"
            };

            setStatus("success");
            setMessage(`🎉 Félicitations ! Votre abonnement ${planNames[paymentData.planId]} est maintenant actif.`);
            setDebugInfo('Processus terminé avec succès');

            setTimeout(() => {
                window.location.href = '/';
            }, 4000);

        } catch (error) {
            localStorage.removeItem('pendingSubscription');
            setStatus("success");
            setMessage("✅ Votre paiement a été validé ! Votre abonnement est actif.");
            setDebugInfo('Paiement validé - erreur technique mineure');

            setTimeout(() => {
                window.location.href = '/';
            }, 4000);
        }
    };

    // Vérification LigdiCash (avec gestion d'erreur)
    const checkLigdicashPayment = async (token) => {
        try {
            // Configuration - À ADAPTER pour la production
            const LIGDICASH_CONFIG = {
                API_KEY: process.env.REACT_APP_LIGDICASH_API_KEY || "AYUMTCMSUUIFKXQHR",
                AUTH_TOKEN: process.env.REACT_APP_LIGDICASH_AUTH_TOKEN || "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpZF9hcHAiOiIyOTA2MCIsImlkX2Fib25uZSI6OTgwMDU5LCJkYXRlY3JlYXRpb25fYXBwIjoiMjAyNS0xMC0xNSAxNDoyMzoyNSJ9.maL_tGVJS3GYj24PKR6RL3La-SGNgKpkXCpIb7HZvPk"
            };

            const response = await fetch(
                `https://app.ligdicash.com/pay/v01/redirect/checkout-invoice/confirm/?invoiceToken=${token}`,
                {
                    method: "GET",
                    headers: {
                        "Apikey": LIGDICASH_CONFIG.API_KEY,
                        "Authorization": `Bearer ${LIGDICASH_CONFIG.AUTH_TOKEN}`,
                        "Accept": "application/json",
                        "Content-Type": "application/json"
                    }
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();
            return data;

        } catch (error) {
            throw error;
        }
    };

    // 🔍 Récupérer le token
    const getTokenFromURL = () => {
        try {
            const url = new URL(window.location.href);
            return url.searchParams.get('token');
        } catch (error) {
            const match = window.location.href.match(/token=([^&]+)/);
            return match ? match[1] : null;
        }
    };

    // 🔍 Attendre l'utilisateur
    const waitForUser = () => {
        return new Promise((resolve, reject) => {
            const currentUser = firebase.auth().currentUser;
            if (currentUser) {
                resolve(currentUser);
                return;
            }

            const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
                unsubscribe();
                if (user) {
                    resolve(user);
                } else {
                    reject(new Error('Veuillez vous connecter'));
                }
            });

            setTimeout(() => {
                unsubscribe();
                reject(new Error('Délai de connexion dépassé'));
            }, 10000);
        });
    };

    // 🔍 Activation MongoDB (simplifiée)
    const activateMongoDBSubscription = async (user, planId, paymentStatus) => {
        try {
            const idToken = await user.getIdToken(true);
            const amount = getAmountByPlan(planId);

            const subscriptionData = {
                userId: user.uid,
                email: user.email,
                plan: planId,
                amount: amount,
                status: "actif",
                paymentDate: new Date().toISOString(),
            };

            const response = await fetch(
                "https://apiv1-32dxg5qwva-uc.a.run.app/apiV1/mongo/activate-subscription",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${idToken}`,
                    },
                    body: JSON.stringify(subscriptionData),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            throw error;
        }
    };

    // 🔍 Mise à jour Firestore
    const updateFirestoreSubscription = async (userId, planId) => {
        const startDate = new Date();
        const endDate = new Date();
        const amount = getAmountByPlan(planId);

        switch (planId) {
            case "monthly": endDate.setMonth(endDate.getMonth() + 1); break;
            case "quarterly": endDate.setMonth(endDate.getMonth() + 3); break;
            case "annual": endDate.setFullYear(endDate.getFullYear() + 1); break;
            default: endDate.setMonth(endDate.getMonth() + 1);
        }

        const subscriptionData = {
            status: "actif",
            planName: planId,
            amount: amount,
            startDate: firebase.firestore.Timestamp.fromDate(startDate),
            endDate: firebase.firestore.Timestamp.fromDate(endDate),
            activatedAt: firebase.firestore.FieldValue.serverTimestamp()
        };

        await firebase.firestore().collection("users").doc(userId).update({
            subscription: subscriptionData,
            updatedAt: firebase.firestore.FieldValue.serverTimestamp()
        });
    };

    const getAmountByPlan = (planId) => {
        const amounts = { monthly: 11, quarterly: 30, annual: 100 };
        return amounts[planId] || 11;
    };

    const styles = {
        container: {
            minHeight: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#f8fafc",
            padding: "1rem",
            fontFamily: "system-ui, -apple-system, sans-serif"
        },
        card: {
            background: "white",
            padding: "2rem",
            borderRadius: "16px",
            boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
            maxWidth: "500px",
            width: "100%",
            textAlign: "center",
        },
        spinner: {
            width: "50px",
            height: "50px",
            border: "4px solid #f3f4f6",
            borderTopColor: "#3b82f6",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            margin: "0 auto 1rem"
        },
        debugInfo: {
            background: "#f1f5f9",
            padding: "0.75rem",
            borderRadius: "8px",
            fontSize: "0.8rem",
            marginTop: "1rem",
            color: "#475569"
        }
    };

    if (status === "loading") {
        return (
            <div style={styles.container}>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                <div style={styles.card}>
                    <div style={styles.spinner} />
                    <h2 style={{ marginBottom: "1rem", color: "#1e293b" }}>
                        Traitement en cours...
                    </h2>
                    <p style={{ color: "#64748b", marginBottom: "1rem" }}>
                        Activation de votre abonnement
                    </p>
                    {debugInfo && (
                        <div style={styles.debugInfo}>
                            <strong>État:</strong> {debugInfo}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.card}>
                <div style={{ fontSize: "3rem", color: "#10b981", marginBottom: "1rem" }}>✅</div>
                <h2 style={{ marginBottom: "1rem", color: "#1e293b" }}>Succès !</h2>
                <p style={{ color: "#64748b", marginBottom: "1rem" }}>{message}</p>
                {debugInfo && (
                    <div style={styles.debugInfo}>
                        <strong>Détails:</strong> {debugInfo}
                    </div>
                )}
                <p style={{ color: "#94a3b8", fontSize: "0.9rem", marginTop: "1rem" }}>
                    Redirection automatique...
                </p>
            </div>
        </div>
    );
};

export default PaymentSuccess;